import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import "@/App.css";
import ScrollToTop from "@/components/ScrollToTop";
import RequireRole from "@/components/admin/RequireRole";
import RequireRecruitmentAccess from "@/components/admin/RequireRecruitmentAccess";
import AppErrorBoundary from "@/components/ui/AppErrorBoundary";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { getRoleHomePath, resolveNavigationPath } from "@/lib/navigation";
import CareersPage from "@/pages/CareersPage";
import HomePage from "@/pages/HomePage";
import JobDetailPage from "@/pages/JobDetailPage";
import MentionsLegalesPage from "@/pages/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "@/pages/PolitiqueConfidentialitePage";
import AdminCandidatures from "@/pages/admin/AdminCandidatures";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import PublicLayout from "@/components/layout/PublicLayout";
import AdminOffres from "@/pages/admin/AdminOffres";
import ClientsPage from "@/pages/admin/pilotage/ClientsPage";
import CollaboratorsPage from "@/pages/admin/pilotage/CollaboratorsPage";
import CollaboratorDetailPage from "@/pages/admin/pilotage/CollaboratorDetailPage";
import CollaboratorsRevenuePage from "@/pages/admin/pilotage/CollaboratorsRevenuePage";
import FixedCostsPage from "@/pages/admin/pilotage/FixedCostsPage";
import MissionDetailPage from "@/pages/admin/pilotage/MissionDetailPage";
import MissionsPage from "@/pages/admin/pilotage/MissionsPage";
import ObjectivesPage from "@/pages/admin/pilotage/ObjectivesPage";
import PilotageDashboardPage from "@/pages/admin/pilotage/PilotageDashboardPage";
import FraisCollaborateursPage from "@/pages/admin/pilotage/FraisCollaborateursPage";
import SimulationPage from "@/pages/admin/pilotage/SimulationPage";
import TimesheetsMensuelsPage from "@/pages/admin/pilotage/TimesheetsMensuelsPage";
import AiConsumptionPage from "@/pages/admin/pilotage/AiConsumptionPage";
import FinancialReconciliationPage from "@/pages/admin/pilotage/FinancialReconciliationPage";

import RecruitmentStatsPage from "@/pages/admin/recruitment/RecruitmentStatsPage";
import CvBuilderPage from "@/pages/admin/recruitment/CvBuilderPage";
import CvLibraryPage from "@/pages/admin/recruitment/CvLibraryPage";
import SettingsPage from "@/pages/admin/settings/SettingsPage";
import CollabHome from "@/pages/collab/CollabHome";
import CollabLayout from "@/pages/collab/CollabLayout";
// Staffing MVP
import NeedsListPage from "@/pages/admin/staffing/NeedsListPage";
import NeedDetailPage from "@/pages/admin/staffing/NeedDetailPage";
import CandidatesListPage from "@/pages/admin/staffing/CandidatesListPage";
import CandidateDetailPage from "@/pages/admin/staffing/CandidateDetailPage";
import InterviewsListPage from "@/pages/admin/staffing/InterviewsListPage";
import PipelinePage from "@/pages/admin/staffing/PipelinePage";
import TalentPoolPage from "@/pages/admin/staffing/TalentPoolPage";
import CommunicationsListPage from "@/pages/admin/staffing/CommunicationsListPage";
import RecruitmentClientsPage from "@/pages/admin/staffing/RecruitmentClientsPage";
import RecruitmentClientDetailPage from "@/pages/admin/staffing/RecruitmentClientDetailPage";
// Candidate portal
import { CandidateAuthProvider } from "@/context/CandidateAuthContext";
import RequireCandidate from "@/components/candidate/RequireCandidate";
import CandidateLayout from "@/components/candidate/CandidateLayout";
import CandidateLoginPage from "@/pages/candidate/CandidateLoginPage";
import CandidateCvPage from "@/pages/candidate/CandidateCvPage";
import CandidateCvInvitationPage from "@/pages/candidate/CandidateCvInvitationPage";

const ADMIN_APP_ROLES = ["ADMIN", "RH"];

const AdminIndexRoute = () => {
  const { role } = useAuth();
  return <Navigate to={getRoleHomePath(role)} replace />;
};

const LegacyRecruitmentRedirect = () => {
  const location = useLocation();
  return <Navigate to={resolveNavigationPath(location.pathname)} replace />;
};

const LegacyMissionRedirect = () => {
  const location = useLocation();
  return <Navigate to={resolveNavigationPath(location.pathname)} replace />;
};

function App() {
  return (
    <div className="App relative">
      <AuthProvider>
      <CandidateAuthProvider>
        <BrowserRouter>
          <AppErrorBoundary>
            <ScrollToTop />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/carrieres" element={<CareersPage />} />
                <Route path="/offre/:jobId" element={<JobDetailPage />} />
                <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                <Route path="/confidentialite" element={<PolitiqueConfidentialitePage />} />
              </Route>

              <Route path="/admin/login" element={<AdminLogin />} />

              <Route
                path="/admin"
                element={(
                  <RequireRole roles={ADMIN_APP_ROLES} fullScreen>
                    <AdminLayout />
                  </RequireRole>
                )}
              >
                <Route index element={<AdminIndexRoute />} />

                <Route path="dashboard" element={<Navigate to="/admin/pilotage/dashboard" replace />} />
                <Route path="collaborateurs" element={<Navigate to="/admin/pilotage/collaborateurs" replace />} />
                <Route path="clients" element={<Navigate to="/admin/pilotage/clients" replace />} />
                <Route path="missions" element={<Navigate to="/admin/pilotage/missions" replace />} />
                <Route path="missions/:missionId" element={<LegacyMissionRedirect />} />
                <Route path="objectifs" element={<Navigate to="/admin/pilotage/objectifs" replace />} />
                <Route path="simulation" element={<Navigate to="/admin/recrutement/simulation" replace />} />
                <Route path="offres" element={<Navigate to="/admin/recrutement/offres" replace />} />
                <Route path="candidatures" element={<Navigate to="/admin/recrutement/candidatures" replace />} />
                <Route path="stats" element={<Navigate to="/admin/recrutement/statistiques" replace />} />
                <Route path="pilotage" element={<Navigate to="/admin/pilotage/dashboard" replace />} />
                <Route
                  path="pilotage/dashboard"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <PilotageDashboardPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/collaborateurs"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <CollaboratorsPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/collaborateurs/:id"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <CollaboratorDetailPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/collaborateurs/revenue"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <CollaboratorsRevenuePage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/clients"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <ClientsPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/missions"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <MissionsPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/timesheets"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <TimesheetsMensuelsPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/frais-collaborateurs"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <FraisCollaborateursPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/objectifs"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <ObjectivesPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/missions/:missionId"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <MissionDetailPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/simulation"
                  element={<Navigate to="/admin/recrutement/simulation" replace />}
                />
                <Route
                  path="pilotage/charges-fixes"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <FixedCostsPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/consommation-ia"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <AiConsumptionPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/rapprochement-financier"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <FinancialReconciliationPage />
                    </RequireRole>
                  )}
                />
                <Route
                  path="pilotage/reconciliation"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <FinancialReconciliationPage />
                    </RequireRole>
                  )}
                />


                <Route path="recrutement" element={<Navigate to="/admin/recrutement/dashboard" replace />} />
                <Route
                  path="recrutement/dashboard"
                  element={(
                    <RequireRecruitmentAccess>
                      <AdminDashboard />
                    </RequireRecruitmentAccess>
                  )}
                />
                <Route
                  path="recrutement/statistiques"
                  element={(
                    <RequireRecruitmentAccess>
                      <RecruitmentStatsPage />
                    </RequireRecruitmentAccess>
                  )}
                />
                <Route
                  path="recrutement/offres"
                  element={(
                    <RequireRecruitmentAccess>
                      <AdminOffres />
                    </RequireRecruitmentAccess>
                  )}
                />
                <Route
                  path="recrutement/candidatures"
                  element={(
                    <RequireRecruitmentAccess>
                      <AdminCandidatures />
                    </RequireRecruitmentAccess>
                  )}
                />
                <Route
                  path="recrutement/postulations"
                  element={<Navigate to="/admin/recrutement/candidatures?tab=public" replace />}
                />
                <Route
                  path="recrutement/candidatures-site"
                  element={<Navigate to="/admin/recrutement/candidatures?tab=public" replace />}
                />
                <Route
                  path="recrutement/cv"
                  element={<Navigate to="/admin/recrutement/cv-library" replace />}
                />
                <Route
                  path="recrutement/cvs"
                  element={<Navigate to="/admin/recrutement/cv-library" replace />}
                />

                <Route
                  path="recrutement/cv-builder/:draftId?"
                  element={(
                    <RequireRecruitmentAccess>
                      <CvBuilderPage />
                    </RequireRecruitmentAccess>
                  )}
                />
                <Route
                  path="recrutement/cv-library"
                  element={(
                    <RequireRecruitmentAccess>
                      <CvLibraryPage />
                    </RequireRecruitmentAccess>
                  )}
                />

                {/* Staffing MVP: Needs */}
                <Route path="recrutement/besoins" element={<RequireRecruitmentAccess><NeedsListPage /></RequireRecruitmentAccess>} />
                <Route path="recrutement/besoins/:needId" element={<RequireRecruitmentAccess><NeedDetailPage /></RequireRecruitmentAccess>} />
                {/* Recruitment V2: Clients & Prospects */}
                <Route path="recrutement/clients" element={<RequireRecruitmentAccess><RecruitmentClientsPage /></RequireRecruitmentAccess>} />
                <Route path="recrutement/clients/:id" element={<RequireRecruitmentAccess><RecruitmentClientDetailPage /></RequireRecruitmentAccess>} />
                {/* Staffing MVP: Candidates */}
                <Route path="recrutement/candidats" element={<RequireRecruitmentAccess><CandidatesListPage /></RequireRecruitmentAccess>} />
                <Route path="recrutement/candidats/:candidateId" element={<RequireRecruitmentAccess><CandidateDetailPage /></RequireRecruitmentAccess>} />
                {/* Staffing MVP: Interviews */}
                <Route path="recrutement/entretiens" element={<RequireRecruitmentAccess><InterviewsListPage /></RequireRecruitmentAccess>} />
                {/* Staffing MVP: Pipeline */}
                <Route path="recrutement/pipeline" element={<RequireRecruitmentAccess><PipelinePage /></RequireRecruitmentAccess>} />
                {/* Recruitment V2: Talent Pool */}
                <Route path="recrutement/vivier" element={<RequireRecruitmentAccess><TalentPoolPage /></RequireRecruitmentAccess>} />
                {/* Recruitment V2: Communications */}
                <Route path="recrutement/communications" element={<RequireRecruitmentAccess><CommunicationsListPage /></RequireRecruitmentAccess>} />

                {/* Recruitment: Simulation de recrutement */}
                <Route
                  path="recrutement/simulation"
                  element={(
                    <RequireRecruitmentAccess>
                      <SimulationPage />
                    </RequireRecruitmentAccess>
                  )}
                />

                <Route
                  path="settings"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <Navigate to="/admin/settings/collab-access" replace />
                    </RequireRole>
                  )}
                />
                <Route
                  path="settings/collab-access"
                  element={(
                    <RequireRole roles={["ADMIN"]}>
                      <SettingsPage />
                    </RequireRole>
                  )}
                />
              </Route>

              <Route
                path="/recrutement/*"
                element={(
                  <RequireRecruitmentAccess fullScreen>
                    <LegacyRecruitmentRedirect />
                  </RequireRecruitmentAccess>
                )}
              />

              <Route
                path="/collab"
                element={(
                  <RequireRole roles={["COLLAB"]} fullScreen>
                    <CollabLayout />
                  </RequireRole>
                )}
              >
                <Route index element={<CollabHome />} />
                <Route path="frais" element={<CollabHome focusSection="expenses" />} />
              </Route>

              {/* Candidate portal - outside admin layout */}
              <Route
                path="/candidat/login"
                element={
                  <CandidateLayout>
                    <CandidateLoginPage />
                  </CandidateLayout>
                }
              />
              <Route
                path="/candidat/mon-cv"
                element={
                  <CandidateLayout>
                    <RequireCandidate>
                      <CandidateCvPage />
                    </RequireCandidate>
                  </CandidateLayout>
                }
              />
              <Route
                path="/candidat/cv/:token"
                element={
                  <CandidateLayout>
                    <CandidateCvInvitationPage />
                  </CandidateLayout>
                }
              />
              <Route
                path="/candidat/cv/session"
                element={
                  <CandidateLayout>
                    <CandidateCvInvitationPage />
                  </CandidateLayout>
                }
              />
            </Routes>
          </AppErrorBoundary>
        </BrowserRouter>
      </CandidateAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
